import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject, Subscription, timer } from 'rxjs';

export const IDLE_WARNING_MS = 59 * 60 * 1000;
export const IDLE_TIMEOUT_MS = 60 * 60 * 1000;
// Test timings: warn with 50s remaining, logout at 1min idle.
// export const IDLE_WARNING_MS = 20 * 1000;
// export const IDLE_TIMEOUT_MS = 30 * 1000;

const ACTIVITY_THROTTLE_MS = 1000;
const LAST_ACTIVITY_KEY = 'idleLastActivityAt';
// Broadcast-only: other tabs use this to extend their own timer.
// Never used to force a stop/logout across tabs.
const ACTIVITY_BROADCAST_KEY = 'idleActivityBroadcastAt';
const ACTIVITY_EVENTS: readonly string[] = [
    'mousedown',
    'keydown',
    'scroll',
    'touchstart',
    'click'
];

@Injectable({
    providedIn: 'root'
})
export class IdleTimeoutService implements OnDestroy {
    readonly warning$: Observable<void>;
    readonly timeout$: Observable<void>;
    readonly warningDismissed$: Observable<void>;

    private readonly warningSubject = new Subject<void>();
    private readonly timeoutSubject = new Subject<void>();
    private readonly warningDismissedSubject = new Subject<void>();
    private readonly onActivity = (): void => this.handleActivity();
    private readonly onVisibilityChange = (): void => this.handleVisibilityChange();
    private readonly onActivityBroadcast = (event: StorageEvent): void =>
        this.handleActivityBroadcast(event);

    private isStarted = false;
    private isWarningOpen = false;
    private lastActivityWrite = 0;
    private warningTimerSub: Subscription | undefined;
    private timeoutTimerSub: Subscription | undefined;

    constructor() {
        this.warning$ = this.warningSubject.asObservable();
        this.timeout$ = this.timeoutSubject.asObservable();
        this.warningDismissed$ = this.warningDismissedSubject.asObservable();
    }

    start(): void {
        if (this.isStarted) {
            this.scheduleFromLastActivity();
            return;
        }
        this.isStarted = true;
        this.isWarningOpen = false;
        this.writeLastActivity(Date.now());
        this.attachListeners();
        this.scheduleFromLastActivity();
    }

    stop(): void {
        if (!this.isStarted) {
            return;
        }
        this.isStarted = false;
        this.isWarningOpen = false;
        this.clearTimers();
        this.detachListeners();
    }

    stayLoggedIn(): void {
        if (!this.isStarted) {
            return;
        }
        this.isWarningOpen = false;
        this.recordActivity(true);
    }

    notifyLogout(): void {
        this.stop();
    }

    getRemainingMs(): number {
        const lastActivity = this.readLastActivity() ?? Date.now();
        return Math.max(0, IDLE_TIMEOUT_MS - (Date.now() - lastActivity));
    }

    ngOnDestroy(): void {
        this.stop();
        this.warningSubject.complete();
        this.timeoutSubject.complete();
        this.warningDismissedSubject.complete();
    }

    private attachListeners(): void {
        ACTIVITY_EVENTS.forEach((eventName) => {
            document.addEventListener(eventName, this.onActivity, true);
        });
        document.addEventListener('visibilitychange', this.onVisibilityChange);
        window.addEventListener('storage', this.onActivityBroadcast);
    }

    private detachListeners(): void {
        ACTIVITY_EVENTS.forEach((eventName) => {
            document.removeEventListener(eventName, this.onActivity, true);
        });
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
        window.removeEventListener('storage', this.onActivityBroadcast);
    }

    private handleActivity(): void {
        if (!this.isStarted || this.isWarningOpen) {
            return;
        }
        this.recordActivity(false);
    }

    private recordActivity(force: boolean): void {
        const now = Date.now();
        if (!force && now - this.lastActivityWrite < ACTIVITY_THROTTLE_MS) {
            return;
        }
        this.lastActivityWrite = now;
        this.writeLastActivity(now);
        this.broadcastActivity(now);
        this.scheduleFromLastActivity();
    }

    private scheduleFromLastActivity(): void {
        this.clearTimers();
        const lastActivity = this.readLastActivity() ?? Date.now();
        const elapsed = Date.now() - lastActivity;
        if (elapsed >= IDLE_TIMEOUT_MS) {
            this.emitTimeout();
            return;
        }
        if (elapsed >= IDLE_WARNING_MS) {
            this.emitWarning();
            this.timeoutTimerSub = timer(IDLE_TIMEOUT_MS - elapsed).subscribe(
                () => this.scheduleFromLastActivity()
            );
            return;
        }
        this.warningTimerSub = timer(IDLE_WARNING_MS - elapsed).subscribe(() =>
            this.scheduleFromLastActivity()
        );
        this.timeoutTimerSub = timer(IDLE_TIMEOUT_MS - elapsed).subscribe(() =>
            this.scheduleFromLastActivity()
        );
    }

    private emitWarning(): void {
        if (!this.isStarted || this.isWarningOpen) {
            return;
        }
        this.isWarningOpen = true;
        this.warningSubject.next();
    }

    private emitTimeout(): void {
        if (!this.isStarted) {
            return;
        }
        this.notifyLogout();
        this.timeoutSubject.next();
    }

    private handleVisibilityChange(): void {
        if (!this.isStarted || document.visibilityState !== 'visible') {
            return;
        }
        this.scheduleFromLastActivity();
    }

    // Extend-only: a sibling tab's activity can push this tab's deadline
    // forward, but it can never cause a stop/logout here.
    private handleActivityBroadcast(event: StorageEvent): void {
        if (
            !this.isStarted ||
            event.key !== ACTIVITY_BROADCAST_KEY ||
            !event.newValue
        ) {
            return;
        }
        const broadcastAt: unknown = JSON.parse(event.newValue);
        if (typeof broadcastAt !== 'number') {
            return;
        }
        const current = this.readLastActivity() ?? 0;
        if (broadcastAt <= current) {
            return;
        }
        this.writeLastActivity(broadcastAt);
        if (this.isWarningOpen) {
            this.isWarningOpen = false;
            this.warningDismissedSubject.next();
        }
        this.scheduleFromLastActivity();
    }

    private writeLastActivity(timestamp: number): void {
        this.writeStorageValue(LAST_ACTIVITY_KEY, timestamp);
    }

    private broadcastActivity(timestamp: number): void {
        try {
            localStorage.setItem(
                ACTIVITY_BROADCAST_KEY,
                JSON.stringify(timestamp)
            );
        } catch {
            // private mode / quota — sibling tabs simply won't be extended
        }
    }

    private writeStorageValue(key: string, value: number): void {
        try {
            sessionStorage.setItem(key, JSON.stringify(value));
        } catch {
            // private mode / quota — timers still run in this tab
        }
    }

    private readLastActivity(): number | null {
        try {
            const raw = sessionStorage.getItem(LAST_ACTIVITY_KEY);
            if (!raw) {
                return null;
            }
            const parsed: unknown = JSON.parse(raw);
            return typeof parsed === 'number' ? parsed : null;
        } catch {
            return null;
        }
    }

    private clearTimers(): void {
        this.warningTimerSub?.unsubscribe();
        this.timeoutTimerSub?.unsubscribe();
        this.warningTimerSub = undefined;
        this.timeoutTimerSub = undefined;
    }
}
